import { Eye, Send } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import type { OfferLanguage } from '@/features/quotes/api/useQuote'
import { OFFER_LANGUAGES } from '@/features/quotes/components/OfferPreviewSheet'
import {
  gapText,
  recipientRefusal,
  recipientState,
} from '@/features/quotes/quoteReadiness'
import { nextRevisionLabel } from '@/features/quotes/quoteText'
import type { CurrentUser, QuoteDetail } from '@/types/domain'

/* The two pieces of the one-sheet quote page (S-3) that exist on no other
 * surface: the client block that opens «Клиент и груз», and the «Предпросмотр»
 * section that closes a draft. Neither fetches anything — both read the record
 * the page already holds, and every act they offer is the page's (the header
 * sheet, the preview sheet, the send sheet), handed in as a callback.
 */

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-text-muted text-[12px] font-light">{label}</dt>
      <dd className="text-text-body min-w-0 text-[13px] break-words">{children}</dd>
    </div>
  )
}

/** Who the quote is for, and what is being shipped — the header's answers in
 *  one compact card. The validity is NOT here: it is a term, and «Условия»
 *  owns it. «Изменить» opens the same `QuoteFormSheet` the header's Edit does,
 *  under the same server refusal. */
export function QuoteClientBlock({
  record,
  user,
  onEdit,
  editBlockedReason,
  editPending,
}: {
  record: QuoteDetail
  user: CurrentUser | undefined
  onEdit: () => void
  editBlockedReason?: string
  editPending?: boolean
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const dash = (value?: string) =>
    value && value.trim() !== '' ? value : t('common.empty')

  /* Where the offer would go. With no address the line says why, and WHICH
     why depends on what the reader may see: a role without contact access is
     served no address and must not be told the client has none. */
  const recipient = recipientState(record, user)

  return (
    <Card>
      <CardHeader
        action={
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('quotes.clientBlock.editLabel')}
            disabled={editPending}
            blockedReason={editBlockedReason}
            onClick={onEdit}
          >
            {t('common.actions.edit')}
          </Button>
        }
      >
        <CardTitle>{t('quotes.clientBlock.title')}</CardTitle>
      </CardHeader>
      <CardBody className="pt-3">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Fact label={t('quotes.columns.client')}>{record.clientName}</Fact>
          <Fact label={t('quotes.form.clientContact')}>{dash(record.clientContactName)}</Fact>
          <Fact label={t('quotes.offerSend.recipient')}>
            {recipient.kind === 'ok' ? (
              recipient.email
            ) : (
              <span className="text-attention text-[12.5px] leading-snug">
                {recipientRefusal(recipient, t)}
              </span>
            )}
          </Fact>
          <Fact label={t('quotes.form.cargoOwner')}>{dash(record.cargoOwner)}</Fact>
          <Fact label={t('quotes.form.cargoDescription')}>
            {dash(record.cargoDescription)}
          </Fact>
          <Fact label={t('quotes.form.quoteDate')}>
            <span className="asa-num">{format.date(record.quoteDate)}</span>
          </Fact>
        </dl>
      </CardBody>
    </Card>
  )
}

/** The last section of a draft: what will go out, in which language, and the
 *  two ways on — look at the document, or send it. The document itself still
 *  opens in `OfferPreviewSheet` (owner decision A): composing it reads the
 *  cargo, the costing and the routings server-side, and a page that did that
 *  on every visit would be composing a document nobody asked to see.
 *
 *  The language is the PAGE's, shared with both sheets — the one fact the
 *  preview and the send must agree on. */
export function QuotePreviewSection({
  record,
  user,
  language,
  onLanguageChange,
  onOpenPreview,
  onSend,
  sendBlockedReason,
  sendPending,
}: {
  record: QuoteDetail
  user: CurrentUser | undefined
  language: OfferLanguage
  onLanguageChange: (language: OfferLanguage) => void
  onOpenPreview: () => void
  onSend: () => void
  sendBlockedReason?: string
  sendPending?: boolean
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const recipient = recipientState(record, user)

  return (
    <Card>
      <CardBody className="flex flex-col gap-4 pt-4">
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('quotes.previewSection.hint')}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <span className="asa-eyebrow">{t('quotes.preview.language')}</span>
          <SegmentedControl
            options={OFFER_LANGUAGES}
            value={language}
            onChange={onLanguageChange}
            label={t('quotes.preview.language')}
          />
        </div>

        <dl className="border-line-hair grid gap-x-6 gap-y-3 border-t pt-4 sm:grid-cols-3">
          <Fact label={t('quotes.offerSend.document')}>
            <span className="asa-num">{record.number}</span>{' '}
            <span className="text-text-muted">
              {t('quotes.offerSend.nextRevision', {
                label: nextRevisionLabel((record.revisions ?? []).length),
              })}
            </span>
          </Fact>
          <Fact label={t('quotes.offerSend.recipient')}>
            {recipient.kind === 'ok' ? (
              recipient.email
            ) : (
              <span className="text-attention">
                {gapText({ code: recipient.kind }, t)}
              </span>
            )}
          </Fact>
          <Fact label={t('quotes.columns.validUntil')}>
            <span className={cn('asa-num', !record.validUntil && 'text-text-muted')}>
              {format.date(record.validUntil)}
            </span>
          </Fact>
        </dl>

        <div className="flex flex-wrap justify-end gap-2">
          {/* Deliberately NOT gated on the send's refusal. A blocked quote is
              precisely when somebody wants to look at the document to find
              out what is wrong, and the sheet shows the refusal above an
              accurate preview rather than withholding both. */}
          <Button variant="secondary" iconLeft={Eye} onClick={onOpenPreview}>
            {t('quotes.previewSection.open')}
          </Button>
          <Button
            iconLeft={Send}
            disabled={sendPending}
            blockedReason={sendBlockedReason}
            onClick={onSend}
          >
            {t('quotes.actions.sendOffer')}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}
